import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Params } from '@angular/router';

export interface Breadcrumb {
  label: string;
  url: string;
  queryParams: Params;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {

  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private _breadcrumbs = signal<Breadcrumb[]>([]);
  breadcrumbs = computed(() => this._breadcrumbs());

  private cachedQueryParams = new Map<string, any>();


  constructor() {
    effect(() => {
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {

        const root = this.router.routerState.snapshot.root;
        this.cacheBreadcrumbParams(root);
        this._breadcrumbs.set(this.buildBreadcrumbs(root));
        this.syncCachedQueryParams();
      });
    });
  }

  private cacheBreadcrumbParams(route: ActivatedRouteSnapshot, parentUrl = '') {
    for (const child of route.children) {
  
      if (child.outlet !== 'primary') continue;
  
      if (child.routeConfig?.path === '') {
        this.cacheBreadcrumbParams(child, parentUrl);
        continue;
      }
  
      const segment = child.url.map(s => s.path).join('/');
      const raw = segment ? `${parentUrl}/${segment}` : parentUrl;
      const path = raw.replace(/\/+/g, '/');
  
      const currentUrl = this.router.url.split('?')[0];

      if (!this.cachedQueryParams.has(path) || currentUrl === path) {
        this.cachedQueryParams.set(path, { ...this.route.snapshot.queryParams });
      }
  
      this.cacheBreadcrumbParams(child, path);
    }
  }

  buildBreadcrumbs(route: ActivatedRouteSnapshot, parentUrl = '', breadcrumbs: Breadcrumb[] = []) {

    for (const child of route.children) {
  
      if (child.outlet !== 'primary') continue;
  
      if (child.routeConfig?.path === '') {
        this.buildBreadcrumbs(child, parentUrl, breadcrumbs);
        continue;
      }
  
      const seg = child.url.map(s => s.path).join('/');
      const raw = seg ? `${parentUrl}/${seg}` : parentUrl;
      const currentUrl = raw.replace(/\/+/g, '/');
  
      const breadcrumb = child.data['breadcrumb'];
      const resolved = child.data['breadcrumbResolver'];
  
      let label = resolved?.label;
  
      if (!label) {
        if (typeof breadcrumb === 'function') {
          label = breadcrumb(child.data);
        } else if (breadcrumb !== false) {
          label = breadcrumb;
        }
      }
  
      if (label && seg) {
  
        breadcrumbs.push({
          label,
          url: currentUrl,
          queryParams: this.cachedQueryParams.get(currentUrl) ?? {}
        });
      }
  
      this.buildBreadcrumbs(child, currentUrl, breadcrumbs);
    }
  
    return breadcrumbs;
  }
  private syncCachedQueryParams() {
    const validUrls = new Set(this.breadcrumbs().map(b => b.url));
    for (const key of Array.from(this.cachedQueryParams.keys())) {
      if (!validUrls.has(key)) {
        this.cachedQueryParams.delete(key);
      }

    }
  }
}
